// /src/pages/BlogPost.js
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSlug from "rehype-slug";
import rehypeAutolinkHeadings from "rehype-autolink-headings";
import { getPostBySlug } from "../data/posts";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const p = await getPostBySlug(slug);
        if (mounted) setPost(p || null);
      } catch (e) {
        console.error(e);
        if (mounted) setErr("Failed to load the post.");
      }
    })();
    return () => { mounted = false; };
  }, [slug]);

  useEffect(() => {
    if (post?.title) document.title = `${post.title} \u2014 Domenic Laurenzi`;
  }, [post]);


  if (err) {
    return (
      <div className="-mt-12 -mx-4">
        <section className="py-12" style={{ background: 'var(--bg)' }}>
          <div className="max-w-7xl mx-auto px-4">
            <div className="max-w-4xl">
              <p className="font-mono text-xs text-[var(--accent)] tracking-wider uppercase mb-4">{'// blog'}</p>
              <h1 className="text-3xl font-bold text-[var(--text)] mb-3">Error</h1>
              <p className="text-[var(--muted)] mb-6">{err}</p>
              <Link to="/blog" className="text-[var(--accent)] font-semibold hover:opacity-90">
                &larr; Back to Blog
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="-mt-12 -mx-4">
        <section className="py-12" style={{ background: 'var(--bg)' }}>
          <div className="max-w-7xl mx-auto px-4">
            <p className="text-[var(--muted)]">Loading&hellip;</p>
          </div>
        </section>
      </div>
    );
  }

  if (!post.content) {
    return (
      <div className="-mt-12 -mx-4">
        <section className="py-12" style={{ background: 'var(--bg)' }}>
          <div className="max-w-7xl mx-auto px-4">
            <div className="max-w-4xl">
              <p className="font-mono text-xs text-[var(--accent)] tracking-wider uppercase mb-4">{'// blog'}</p>
              <h1 className="text-3xl font-bold text-[var(--text)] mb-3">Post not found</h1>
              <Link to="/blog" className="text-[var(--accent)] font-semibold hover:opacity-90">
                &larr; Back to Blog
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Tailwind-friendly markdown renderers
  const mdComponents = {
    h1: ({ node, ...props }) => (
      <h1 {...props} className="text-4xl font-extrabold tracking-tight mt-8 mb-4 first:mt-0">
        {props.children}
      </h1>
    ),
    h2: ({ node, ...props }) => (
      <h2 {...props} className="text-3xl font-bold mt-10 mb-4 border-b pb-2 border-[color:var(--border)]">
        {props.children}
      </h2>
    ),
    h3: ({ node, ...props }) => (
      <h3 {...props} className="text-2xl font-semibold mt-8 mb-3">
        {props.children}
      </h3>
    ),
    h4: ({ node, ...props }) => (
      <h4 {...props} className="text-xl font-semibold mt-6 mb-2">
        {props.children}
      </h4>
    ),
    p: ({ node, ...props }) => (
      <p {...props} className="leading-8 my-4">
        {props.children}
      </p>
    ),
    ul: ({ node, ...props }) => (
      <ul {...props} className="list-disc pl-6 my-4 space-y-2">
        {props.children}
      </ul>
    ),
    ol: ({ node, ...props }) => (
      <ol {...props} className="list-decimal pl-6 my-4 space-y-2">
        {props.children}
      </ol>
    ),
    li: ({ node, ...props }) => (
      <li {...props} className="pl-1">
        {props.children}
      </li>
    ),
    blockquote: ({ node, ...props }) => (
      <blockquote {...props} className="border-l-4 pl-4 italic my-6 border-[color:var(--border)]">
        {props.children}
      </blockquote>
    ),
    code: ({ inline, className, children, ...props }) =>
      inline ? (
        <code className="px-1.5 py-0.5 rounded bg-[var(--bg2)] border border-[color:var(--border)]">
          {children}
        </code>
      ) : (
        <pre className="my-6 p-4 rounded-lg overflow-x-auto bg-[var(--bg2)] border border-[color:var(--border)]">
          <code className={className} {...props}>
            {children}
          </code>
        </pre>
      ),
    table: ({ node, ...props }) => (
      <div className="my-6 overflow-x-auto">
        <table {...props} className="w-full border-collapse">
          {props.children}
        </table>
      </div>
    ),
    th: ({ node, ...props }) => (
      <th {...props} className="text-left font-semibold px-3 py-2 border-b border-[color:var(--border)]">
        {props.children}
      </th>
    ),
    td: ({ node, ...props }) => (
      <td {...props} className="px-3 py-2 border-b border-[color:var(--border)]">
        {props.children}
      </td>
    ),
    a: ({ node, href = "", ...props }) => {
      const isExternal = /^https?:\/\//i.test(href);
      return (
        <a
          href={href}
          {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          className="text-[var(--accent)] underline hover:opacity-90"
        >
          {props.children}
        </a>
      );
    },
    img: ({ node, alt, ...props }) => (
      <img
        {...props}
        alt={alt ?? ""}
        loading="lazy"
        className="rounded-lg shadow-lg my-6 mx-auto"
      />
    ),
    hr: () => <hr className="my-10 border-[color:var(--border)]" />,
  };

  return (
    <div className="-mt-12 -mx-4">
      {/* Post header */}
      <section className="py-12" style={{ background: 'var(--bg)' }}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-4xl">
            <p className="font-mono text-xs text-[var(--accent)] tracking-wider uppercase mb-4">{'// blog'}</p>

            {post.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {post.tags.map((t) => (
                  <span
                    key={t}
                    className="px-2.5 py-1 rounded-full text-xs font-semibold"
                    style={{ background: "var(--tag-bg)", color: "var(--tag-text)" }}
                  >
                    {t}
                  </span>
                ))}
              </div>
            )}

            <h1 className="text-4xl font-extrabold tracking-tight leading-tight mb-2 text-[var(--text)]">
              {post.title}
            </h1>

            <p className="text-sm text-[var(--muted)]">
              {post.date} {post.readTime ? `\u2022 ${post.readTime}` : ""}
            </p>
          </div>
        </div>
      </section>

      <hr className="section-rule" />

      {/* Markdown body */}
      <section className="py-10" style={{ background: 'var(--bg2)' }}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-4xl text-[var(--text)]">
            <div className="prose prose-neutral dark:prose-invert max-w-none">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[
                  rehypeSlug,
                  [rehypeAutolinkHeadings, { behavior: "wrap", properties: { className: ["no-underline"] } }],
                ]}
                components={mdComponents}
              >
                {post.content}
              </ReactMarkdown>
            </div>

            {/* Back link */}
            <div className="mt-10 pt-6 border-t border-[color:var(--border)]">
              <Link to="/blog" className="text-[var(--accent)] font-semibold hover:opacity-90">
                &larr; Back to Blog
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
